import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import PersonIcon from '@material-ui/icons/Person';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import axios from 'axios';
import StoreContext from '../../Store/context';
import { API_BASE_URL } from '../../../utils/useApi';
import { usePOS } from '../../../contexts/POSContext';
import UIDialogTitle from '../../UI/Dialog/Title/UIDialogTitle';
import { normalizeText } from '../../../utils/utils';
import ClientCreateModal from '../../Client/ClientCreateModal';

const WINDOW_SIZE = 50;
const ADVANCE_STEP = 50;
const REMOVE_STEP = 25;

const POSCustomerModal = ({ open, onClose }) => {
  const { credencials } = useContext(StoreContext);
  const { frequentCustomers, selectedCustomer, setSelectedCustomer, isDarkMode } = usePOS();

  const [searchTerm, setSearchTerm] = useState('');
  const [remoteResults, setRemoteResults] = useState([]);
  const [loadingRemote, setLoadingRemote] = useState(false);
  const [range, setRange] = useState({ start: 0, end: WINDOW_SIZE });
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const inputRef = useRef(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setRemoteResults([]);
      setRange({ start: 0, end: WINDOW_SIZE });
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 100);
    }
  }, [open]);

  // Reseta paginação ao pesquisar
  useEffect(() => {
    setRange({ start: 0, end: WINDOW_SIZE });
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [searchTerm]);

  // Busca remota quando houver termo
  useEffect(() => {
    if (!searchTerm.trim()) {
      setRemoteResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoadingRemote(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/customers`, {
          headers: {
            sessionToken: credencials?.sessionToken,
            company: String(credencials?.company || ''),
          },
          params: { name: searchTerm.trim() },
        });
        setRemoteResults(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setRemoteResults([]);
      } finally {
        setLoadingRemote(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchTerm, credencials]);

  // Lista total filtrada multi-variável (nome, sobrenome, CPF, telefone, id)
  const fullList = useMemo(() => {
    if (!searchTerm.trim()) {
      return frequentCustomers || [];
    }
    const term = normalizeText(searchTerm);
    const baseList = remoteResults.length > 0 ? remoteResults : (frequentCustomers || []);
    return baseList.filter((cust) => {
      const fullName = normalizeText(`${cust.name || ''} ${cust.lastName || cust.last_name || ''}`);
      const cpf = normalizeText(cust.cpf);
      const phone = normalizeText(cust.phone || cust.whatsapp);
      const id = String(cust.id);
      return fullName.includes(term) || cpf.includes(term) || phone.includes(term) || id.includes(term);
    });
  }, [searchTerm, remoteResults, frequentCustomers]);

  // Janela Deslizante (+50 / -25)
  const displayedList = useMemo(() => {
    return fullList.slice(range.start, range.end);
  }, [fullList, range]);

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;

    // Rolagem para baixo: avança +50 e descarta os 25 primeiros
    if (scrollTop + clientHeight >= scrollHeight - 40) {
      if (range.end < fullList.length) {
        setRange((prev) => {
          const nextEnd = Math.min(prev.end + ADVANCE_STEP, fullList.length);
          const nextStart = Math.min(prev.start + REMOVE_STEP, Math.max(0, nextEnd - WINDOW_SIZE));
          return { start: nextStart, end: nextEnd };
        });
      }
    }

    // Rolagem para cima: recupera os anteriores
    if (scrollTop <= 30 && range.start > 0) {
      setRange((prev) => {
        const nextStart = Math.max(0, prev.start - REMOVE_STEP);
        const nextEnd = Math.max(WINDOW_SIZE, prev.end - REMOVE_STEP);
        return { start: nextStart, end: nextEnd };
      });
    }
  };

  const handleSelect = (customer) => {
    setSelectedCustomer(customer);
    onClose();
  };

  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: isDarkMode ? 'pos-dark-paper' : '' }}
    >
      <UIDialogTitle onClose={onClose}>
        Identificar Cliente [F7]
      </UIDialogTitle>

      <DialogContent dividers style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 16px' }}>
        <TextField
          inputRef={inputRef}
          placeholder="Digite o nome, CPF ou telefone do cliente..."
          variant="outlined"
          fullWidth
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && displayedList.length > 0) {
              e.preventDefault();
              handleSelect(displayedList[0]);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: loadingRemote ? <CircularProgress size={20} /> : null,
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.82rem', color: isDarkMode ? '#94a3b8' : '#616161' }}>
            {searchTerm.trim() ? `Resultados (${fullList.length})` : `Clientes Frequentes (${fullList.length})`}
            {fullList.length > 0 && ` • Exibindo ${range.start + 1} - ${Math.min(range.end, fullList.length)}`}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<PersonAddIcon style={{ fontSize: 16 }} />}
              onClick={() => setOpenCreateModal(true)}
              style={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                borderColor: isDarkMode ? '#38bdf8' : '#17497F',
                color: isDarkMode ? '#38bdf8' : '#17497F',
              }}
            >
              + Novo Cliente
            </Button>
            {selectedCustomer && (
              <Button size="small" onClick={handleClearCustomer} style={{ color: '#ef4444', textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }}>
                Remover Identificação
              </Button>
            )}
          </div>
        </div>

        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          style={{
            maxHeight: 340,
            overflowY: 'auto',
            border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
            borderRadius: 4,
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {/* Indicador de itens anteriores removidos do topo */}
              {range.start > 0 && (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', padding: '6px 12px', backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.5)' : '#f8fafc', color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '0.78rem' }}>
                    ▲ Role para cima para ver os primeiros {range.start} clientes
                  </td>
                </tr>
              )}

              {displayedList.map((cust) => {
                const isSelected = selectedCustomer?.id === cust.id;
                return (
                  <tr
                    key={cust.id}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isSelected
                        ? (isDarkMode ? 'rgba(56, 189, 248, 0.15)' : '#e8f0fe')
                        : 'transparent',
                      borderBottom: isDarkMode ? '1px solid #1e293b' : '1px solid #f0f0f0',
                      transition: 'background-color 0.15s ease',
                    }}
                    onClick={() => handleSelect(cust)}
                  >
                    <td style={{ width: 44, padding: '8px 10px', textAlign: 'center' }}>
                      <PersonIcon style={{ color: isSelected ? (isDarkMode ? '#38bdf8' : '#17497F') : (isDarkMode ? '#64748b' : '#9e9e9e') }} />
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ fontWeight: 600, color: isSelected ? (isDarkMode ? '#38bdf8' : '#17497F') : (isDarkMode ? '#f8fafc' : '#212121'), fontSize: '0.88rem' }}>
                        {cust.name} {cust.lastName || ''}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : '#757575', marginTop: 2 }}>
                        {cust.cpf ? `CPF: ${cust.cpf}` : ''} {cust.phone ? `• Tel: ${cust.phone}` : ''}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', padding: '8px 12px', width: 110 }}>
                      <Button
                        size="small"
                        variant={isSelected ? 'contained' : 'outlined'}
                        style={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: isSelected ? '#17497F' : 'transparent',
                          color: isSelected ? '#ffffff' : (isDarkMode ? '#38bdf8' : '#17497F'),
                          borderColor: isDarkMode ? '#38bdf8' : '#17497F',
                        }}
                      >
                        {isSelected ? 'Selecionado' : 'Selecionar'}
                      </Button>
                    </td>
                  </tr>
                );
              })}

              {displayedList.length === 0 && !loadingRemote && (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', padding: 28, color: isDarkMode ? '#94a3b8' : '#9e9e9e' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                      <span>Nenhum cliente encontrado.</span>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setOpenCreateModal(true)}
                        style={{
                          textTransform: 'none',
                          backgroundColor: '#17497F',
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.82rem',
                          padding: '6px 14px',
                        }}
                      >
                        + Cadastrar {searchTerm.trim() ? `"${searchTerm.trim()}"` : 'novo cliente'}
                      </Button>
                    </div>
                  </td>
                </tr>
              )}

              {/* Indicador de rolagem no rodapé */}
              {range.end < fullList.length && (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', padding: 10, color: '#94a3b8', fontSize: '0.8rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <CircularProgress size={16} />
                      Role para carregar mais clientes (+{fullList.length - range.end} restantes)
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>

      <DialogActions style={{ padding: '8px 16px', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '0.75rem', color: isDarkMode ? '#94a3b8' : '#757575' }}>
          Dica: Pressione <strong>Enter</strong> para selecionar o 1º cliente ou clique na linha.
        </span>
        <Button onClick={onClose} color="default">
          Fechar [Esc]
        </Button>
      </DialogActions>

      <ClientCreateModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        initialName={searchTerm.trim()}
        onSuccess={(createdCustomer) => {
          setSelectedCustomer(createdCustomer);
          setOpenCreateModal(false);
          onClose();
        }}
      />
    </Dialog>
  );
};

export default POSCustomerModal;
